import React, { useEffect, useRef, useState } from "react";

import { GENERAL_ERROR_MESSAGE } from "../../utils/message";
import { XCrossIcon } from "../../assets";
import { postApi } from "../../apis/api";
import { useMessage } from "../../context/MessageContext";
import {
  IMPORT_ACTIVE_CUSTOMER,
  IMPORT_AGENT,
  IMPORT_AGENT_HUB,
  IMPORT_AGENT_RATE,
  IMPORT_FSC_AGENT,
  IMPORT_MILE_ZONE,
  IMPORT_WEIGHT_ZONE,
  IMPORT_ZIP,
  IMPORT_CUSTOMER_LIST,
  IMPORT_CUSTOMER_RATE_LIST,
  IMPORT_CUSTOMER_FSC_LIST,
  IMPORT_AGENT_ADDITIONAL_CHARGES,
  IMPORT_CUSTOMER_ADDITIONAL_CHARGES,
  IMPORT_CUSTOMER_TECH_FEE,
  IMPORT_SCAC,
  IMPORT_MASTER_ZIP_CODE,
} from "../../apis/end-points";
import { Loader } from "../loaders/loaders";
import { useNavigate } from "react-router-dom";
import { globalMaxFileSize } from "../../utils/utils";

interface DocumentInfoType {
  file: any;
}
interface AddDocumentURLType {
  //   confirmModal: boolean;
  setConfirmModal: (value: boolean) => void;
  popUpTitle: string;
  changeStep?: (hubStep: number, data: any) => void | any;
  importApiUrl?: string;
}

// Accepted file types and maximum file size constants
const acceptedFileTypes = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // XLSX
  "application/vnd.ms-excel", // XLS (older Excel format)
  "text/csv", // CSV
];
const maxFileSize = globalMaxFileSize;

/**
 * AddDocumentURLModal Component
 *
 * This component allows users to upload documents or add URLs. It includes file validation,
 * drag-and-drop functionality, and URL validation. The user can upload files and add URLs,
 * and the component will handle validating and displaying the uploaded files and added URLs.
 *
 * @param confirmModal - Boolean to control the visibility of the modal.
 * @param setConfirmModal - Function to set the visibility of the modal.
 * @param documentInfo - Object containing the initial files and URLs.
 * @param setDocumentInfo - Function to update the files and URLs.
 */
const UploadAgent: React.FC<AddDocumentURLType> = ({
  setConfirmModal,
  popUpTitle,
  changeStep,
  importApiUrl,
}) => {
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formState, setFormState] = useState<DocumentInfoType>({ file: null });
  const [loading, setLoading] = useState<boolean>(false);
  const { setMessage } = useMessage();

  /**
   * Handles closing the modal by setting the confirmModal state to false.
   */
  const handleClose = () => {
    setConfirmModal(false);
  };

  /**
   * Validates the file type and size.
   * @param file - The file to validate.
   * @returns - Error message string if validation fails, otherwise an empty string.
   */
  const validateFile = (file: File) => {
    if (!acceptedFileTypes.includes(file?.type)) {
      return `${file.name} is not a valid file type.`;
    }
    if (file.size > maxFileSize) {
      return `File size ${(file.size / 1024 / 1024).toFixed(2)
        } MB exceeds the maximum allowed size of ${maxFileSize / 1024 / 1024} MB.`;
    }
    return true;
  };

  /**
   * Handles file selection through the input element.
   * Validates each file and adds it to the form state if it passes validation.
   * @param event - The file input change event.
   */
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const file = files?.[0]; // Only take the first file (single file upload)
    // Validate file type and size
    const isValidFile = validateFile(file);
    if (isValidFile !== true) {
      setFileErrors([isValidFile]);
      return;
    }
    // Update state with a single file
    setFormState({
      ...formState,
      file: file,
    });

    // Clear errors
    setFileErrors([]);
  };

  // Function to remove file

  /**
   * Handles file drop event in the drag-and-drop area.
   * Validates each file and adds it to the form state if it passes validation.
   * @param event - The drag event.
   */
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();

    // Extracting only the first file
    const file = event.dataTransfer.files?.[0];
    if (!file) return;

    // File validation (You can use `validateFile(file)` here)
    const newFileErrors: string[] = [];
    const existingFileName = formState.file?.fileName;

    // // Validate file type and size
    // Validate file type and size
    const isValidFile = validateFile(file);
    if (isValidFile !== true) {
      setFileErrors([isValidFile]);
      return;
    }

    // Check if the file is already uploaded
    if (existingFileName && existingFileName === file.name) {
      newFileErrors.push(`${file.name} has already been uploaded.`);
    } else {
      // Replace the file with the new one
      setFormState({
        ...formState,
        file,
      });
    }

    setFileErrors(newFileErrors);
  };

  /**
   * Removes a file from the form state.
   * @param fileName - The name of the file to remove.
   */
  const handleRemoveFile = () => {
    setFormState({
      ...formState,
      file: null,
    });
  };

  /**
   * Saves the form state to the parent component and closes the modal.
   */
  const handleSave = async () => {
    if (!formState.file) {
      alert("Please upload a file");
      return;
    }
    try {
      setLoading(true);

      // Prepare form data with the file
      const formData = new FormData();
      formData.append("file", formState.file);
      let uploadResp;
      if (popUpTitle.includes("Pickup Zip")) {
        formData.append("type", "pickup");
        uploadResp = await postApi("/import/zip/internaltlzipcode", formData);
      } else if (popUpTitle.includes("Drop Zip")) {
        formData.append("type", "drop");
        uploadResp = await postApi("/import/zip/internaltlzipcode", formData);
      } else if (popUpTitle.includes("Service Zip codes")) {
        uploadResp = await postApi(IMPORT_ZIP, formData);
      } else if (popUpTitle.includes("Agent Hub")) {
        uploadResp = await postApi(IMPORT_AGENT_HUB, formData);
      } else if (popUpTitle.includes("Mile Zone")) {
        uploadResp = await postApi(IMPORT_MILE_ZONE, formData);
      } else if (popUpTitle.includes("Agents")) {
        uploadResp = await postApi(IMPORT_AGENT, formData);
      } else if (popUpTitle.includes("Weight Zone")) {
        uploadResp = await postApi(IMPORT_WEIGHT_ZONE, formData);
      } else if (popUpTitle.includes("FSC Agent")) {
        uploadResp = await postApi(importApiUrl ?? IMPORT_FSC_AGENT, formData);
      } else if (popUpTitle.includes("Agent Rate")) {
        if (popUpTitle.includes("LTL Agent Rate")) {
          formData.append("type", "LTL");
        }
        uploadResp = await postApi(IMPORT_AGENT_RATE, formData);
      } else if (popUpTitle.includes("Customers")) {
        uploadResp = await postApi(IMPORT_CUSTOMER_LIST, formData);
      } else if (popUpTitle.includes("Customer Rate")) {
        uploadResp = await postApi(IMPORT_CUSTOMER_RATE_LIST, formData);
      } else if (popUpTitle.includes("Customer FSC List")) {
        uploadResp = await postApi(IMPORT_CUSTOMER_FSC_LIST, formData);
      } else if (popUpTitle.includes("Agent Additional Charges")) {
        uploadResp = await postApi(IMPORT_AGENT_ADDITIONAL_CHARGES, formData);
      } else if (popUpTitle.includes("Customer Additional Charges")) {
        uploadResp = await postApi(
          IMPORT_CUSTOMER_ADDITIONAL_CHARGES,
          formData
        );
      } else if (popUpTitle.includes("Customer Tech Fee")) {
        uploadResp = await postApi(IMPORT_CUSTOMER_TECH_FEE, formData);
      } else if (popUpTitle.includes("SCAC")) {
        uploadResp = await postApi(IMPORT_SCAC, formData);
      } else if (popUpTitle.includes("ZipCodes")) {
        uploadResp = await postApi(IMPORT_MASTER_ZIP_CODE, formData);
      } else {
        uploadResp = await postApi(IMPORT_ACTIVE_CUSTOMER, formData);
      }
      if (uploadResp.success) {
        const updatedmsg = uploadResp.message
          ? uploadResp.message
          : `${popUpTitle} uploaded successfully!`;
        setLoading(false);
        setMessage(`${updatedmsg}`, "success");
        handleClose();
        if (popUpTitle.includes("SCAC")) {
          navigate("/system-configuration/scac");
        }
        setTimeout(() => {
          //!Need to work on this naviagtion--------------------------------------------------------
          if (
            popUpTitle.includes("Weight Zone") ||
            popUpTitle.includes("Agent Rate") ||
            popUpTitle.includes("Mile Zone")
          ) {
            navigate("/agent-list");
          } else {
            changeStep && changeStep(1, {});
          }
        }, 3000);
      } else {
        setLoading(false);
        setMessage(uploadResp.error?.message ?? GENERAL_ERROR_MESSAGE, "error");
      }
    } catch (error) {
      setLoading(false);
      setMessage(GENERAL_ERROR_MESSAGE, "error");
    }
    handleClose();
  };

  const handleClick = () => {
    // Clear the input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="modal modal--hazmatDetail modal--open">
      <div className="modal__dialog">
        <div className="modal__content modal__content--lg">
          <div className="modal__header">
            <h4 className="modal__header--title">Upload {popUpTitle}</h4>
            <button
              className="btn btn__transparent btnClose"
              onClick={handleClose}
            >
              <XCrossIcon />
            </button>
          </div>
          <div className="modal__body">
            <div
              className="formRow"
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
            >
              <div className="formCol">
                <div className="form__Field">
                  <label className="form__label">
                    Select files or drop files here
                  </label>
                  <label
                    htmlFor="UploadFile"
                    className="form__browsefile form__browsefile--orderUrl"
                  >
                    <input
                      type="file"
                      id="UploadFile"
                      className="form__uploadfiled"
                      //multiple
                      onChange={handleFileChange}
                      ref={fileInputRef}
                      onClick={handleClick}
                    />

                    <span className="btn btn__primary">Browse File</span>
                    {!formState.file && (
                      <span className="form__browsefile__url">
                        No file chosen
                      </span>
                    )}
                  </label>
                  {fileErrors.length > 0 && (
                    <div className="form__error">
                      {fileErrors.map((error, index) => (
                        <div key={index}>{error}</div>
                      ))}
                    </div>
                  )}
                </div>

                {formState.file && (
                  <div className="browsefile__container">
                    {/* {formState.files?.map((file) => ( */}
                    <div className="browsefile__container__files">
                      <div key={formState?.file?.name} className="file__item">
                        {formState?.file.name}
                      </div>
                      <button
                        className="btn btn__danger"
                        onClick={() => handleRemoveFile()}
                      >
                        Remove
                      </button>
                    </div>
                    {/* ))} */}
                  </div>
                )}
              </div>
              <span>Upload one file (XLSX, CSV, XLS) up to {maxFileSize / 1024 / 1024} MB.</span>
            </div>
          </div>
          <div className="modal__footer">
            <div className="btn__group">
              <button className="btn btn__white" onClick={handleClose}>
                Cancel
              </button>
              <button
                className="btn btn__primary"
                disabled={!formState.file}
                onClick={handleSave}
              >
                Upload
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal__backdrop"></div>
      {loading && <Loader />}
    </div>
  );
};

export default UploadAgent;
